<div class="ah-root">
  <header class="ah-top">
    <button type="button" class="ah-icon-btn" (click)="onBack()" aria-label="Back to chat" title="Back to chat">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M15 18l-6-6 6-6" />
      </svg>
    </button>
    <div class="ah-top-copy">
      <h1>Agent hooks</h1>
      <p class="ah-subtitle">Connect this app's events to agents built in DPE. A new agent needs no release: pick it here.</p>
    </div>
    <button type="button" class="ah-ghost-btn" (click)="reload()" [disabled]="isLoading" title="Refresh">Refresh</button>
  </header>

  <div class="ah-alert" *ngIf="error" role="alert">{{ error }}</div>
  <div class="ah-alert ah-alert--info" *ngIf="forbidden" role="alert">Only an admin of this workspace can manage agent hooks.</div>

  <div class="ah-loading" *ngIf="isLoading"><div class="ah-spinner"></div><p>Loading…</p></div>

  <ng-container *ngIf="!isLoading && !forbidden && !error">

    <!-- 1 · Connection -->
    <section class="ah-card" aria-labelledby="ah-conn">
      <div class="ah-card-head">
        <h2 id="ah-conn"><span class="ah-step">1</span> Connection to DPE</h2>
        <span class="ah-pill" [class.ah-pill--ok]="test?.ok" [class.ah-pill--bad]="test && !test.ok">
          {{ test ? (test.ok ? 'Connected · ' + test.triggers.length + ' trigger' + (test.triggers.length === 1 ? '' : 's') : 'Not connected') : (connected ? 'Key saved' : 'Not set up') }}
        </span>
      </div>
      <p class="ah-help">DPE shows this workspace's address and a connection key under <b>Agents → Inbound triggers → App connections</b>. Paste them here once.</p>

      <div class="ah-grid">
        <label class="ah-field">
          <span>DPE address</span>
          <input type="text" [(ngModel)]="addressInput" [placeholder]="integration?.usingDefaultAddress ? integration?.dpeApiBaseUrl || 'https://dpe.example.com/engineapi/api' : 'https://dpe.example.com/engineapi/api'" />
          <small *ngIf="integration?.usingDefaultAddress && integration?.dpeApiBaseUrl">Blank uses this server's configured address.</small>
        </label>
        <label class="ah-field">
          <span>Connection key</span>
          <input type="password" autocomplete="off" [(ngModel)]="keyInput" [placeholder]="integration?.hasKey ? 'Saved (' + integration?.keyPrefix + '…). Type to replace it.' : 'dpe_…'" />
          <small *ngIf="integration?.hasKey">Stored encrypted. <button type="button" class="ah-link" (click)="removeKey()">Remove</button></small>
        </label>
      </div>

      <div class="ah-actions">
        <button type="button" class="ah-btn ah-btn--primary" (click)="saveConnection()" [disabled]="isSavingConnection">{{ isSavingConnection ? 'Saving…' : 'Save connection' }}</button>
        <button type="button" class="ah-btn" (click)="runTest(false)" [disabled]="isTesting || !connected">{{ isTesting ? 'Testing…' : 'Test connection' }}</button>
        <span class="ah-note ah-note--bad" *ngIf="test && !test.ok">{{ test.message }}</span>
      </div>
    </section>

    <!-- 2 · Hooks -->
    <section class="ah-card" aria-labelledby="ah-hooks">
      <div class="ah-card-head"><h2 id="ah-hooks"><span class="ah-step">2</span> Which agent handles which event</h2></div>
      <p class="ah-help">When the event happens in this app, the data is sent to the agent you choose. Nothing is sent for an event with no hook.</p>

      <div class="ah-empty" *ngIf="hooks.length === 0">No hooks yet. Add one below.</div>

      <article class="ah-hook" *ngFor="let h of hooks" [class.ah-hook--off]="!h.enabled">
        <div class="ah-hook-main">
          <div class="ah-hook-title">{{ eventTitle(h.eventName) }} <span class="ah-arrow">→</span> <b>{{ trigger(h.triggerSlug)?.name || h.triggerSlug }}</b></div>
          <div class="ah-hook-sub">
            <code>{{ h.eventName }}</code>
            <span *ngIf="trigger(h.triggerSlug)?.agentName"> · agent “{{ trigger(h.triggerSlug)?.agentName }}”</span>
            <span *ngIf="trigger(h.triggerSlug)?.description"> · {{ trigger(h.triggerSlug)?.description }}</span>
          </div>
          <div class="ah-note ah-note--warn" *ngIf="missingInCatalog(h)">DPE no longer lists “{{ h.triggerSlug }}”. The agent may have been removed or renamed there.</div>
        </div>
        <div class="ah-hook-controls">
          <label class="ah-check" title="The agent's tools then act as the person who triggered the event">
            <input type="checkbox" [ngModel]="h.forwardUserToken" (ngModelChange)="change(h, { forwardUserToken: $event })" [disabled]="busyHookId === h.id" />
            Pass on the user's sign-in
          </label>
          <label class="ah-check">
            <input type="checkbox" [ngModel]="h.enabled" (ngModelChange)="change(h, { enabled: $event })" [disabled]="busyHookId === h.id" />
            On
          </label>
          <button type="button" class="ah-btn ah-btn--small" (click)="sendTest(h)" [disabled]="busyHookId === h.id">Send a sample</button>
          <button type="button" class="ah-btn ah-btn--small ah-btn--danger" (click)="remove(h)" [disabled]="busyHookId === h.id">Remove</button>
        </div>
      </article>

      <div class="ah-add">
        <h3>Connect an event</h3>
        <div class="ah-grid ah-grid--3">
          <label class="ah-field">
            <span>When this happens</span>
            <select [(ngModel)]="newEvent">
              <option *ngFor="let e of events" [value]="e.name">{{ e.title }}</option>
            </select>
          </label>
          <label class="ah-field">
            <span>Send it to</span>
            <select *ngIf="catalog.length; else manualTrigger" [(ngModel)]="newTrigger">
              <option value="" disabled>Choose an agent trigger…</option>
              <option *ngFor="let t of catalog" [value]="t.slug">{{ t.name }}{{ t.agentName ? ' · ' + t.agentName : '' }}</option>
            </select>
            <ng-template #manualTrigger>
              <input type="text" [(ngModel)]="newTrigger" placeholder="trigger address, e.g. leave-submitted" />
              <small>Test the connection above to pick from a list instead.</small>
            </ng-template>
          </label>
          <label class="ah-check ah-check--block">
            <input type="checkbox" [(ngModel)]="newForward" />
            <span>Pass on the user's sign-in <small>so the agent's tools act as that person</small></span>
          </label>
        </div>
        <p class="ah-help" *ngIf="newEvent">{{ eventDescription(newEvent) }}
          <button type="button" class="ah-link" (click)="toggleSample(newEvent)">{{ openSample === newEvent ? 'Hide' : 'What the agent receives' }}</button></p>
        <pre class="ah-code" *ngIf="openSample === newEvent">{{ sampleJson(newEvent) }}</pre>
        <div class="ah-actions">
          <button type="button" class="ah-btn ah-btn--primary" (click)="addHook()" [disabled]="!canAdd">{{ isAdding ? 'Adding…' : 'Add hook' }}</button>
        </div>
      </div>
    </section>

    <!-- 3 · Activity -->
    <section class="ah-card" aria-labelledby="ah-activity">
      <div class="ah-card-head">
        <h2 id="ah-activity"><span class="ah-step">3</span> Recent activity</h2>
        <button type="button" class="ah-ghost-btn" (click)="refreshDeliveries()">Refresh</button>
      </div>
      <div class="ah-empty" *ngIf="deliveries.length === 0">Nothing sent yet. Events show up here with what DPE answered.</div>
      <div class="ah-table" *ngIf="deliveries.length">
        <div class="ah-row ah-row--head"><span>When</span><span>Event → trigger</span><span>Item</span><span>Result</span></div>
        <div class="ah-row" *ngFor="let d of deliveries">
          <span class="ah-muted">{{ ago(d.at) }}</span>
          <span><code>{{ d.eventName }}</code> → {{ d.triggerSlug }}</span>
          <span class="ah-muted ah-ellipsis" [title]="d.subject || ''">{{ d.subject || '—' }}</span>
          <span>
            <span class="ah-pill" [class.ah-pill--ok]="d.status === 'sent'" [class.ah-pill--bad]="d.status === 'failed'" [class.ah-pill--warn]="d.status === 'skipped'">{{ d.status }}{{ d.httpStatus ? ' · ' + d.httpStatus : '' }}</span>
            <span class="ah-note ah-note--bad" *ngIf="d.error">{{ d.error }}</span>
          </span>
        </div>
      </div>
    </section>
  </ng-container>
</div>
